import { Icon } from "@mdi/react";
import * as Sentry from "@sentry/react";
import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import { mdiChevronDown } from "@mdi/js";
import { Popover } from "antd";
import { widgetHoverAtom } from "atoms/interaction";
import classnames from "classnames";
import { IS_AWS_ENV } from "constants/app.constants";
import { EvaChart, RigFracActivityMetric } from "constants/charts.enums";
import { useChartsContext } from "contexts/ChartContext";
import { DEFAULT_RIG_FRAC_ACTIVITY_REQUEST } from "entities/charts/eva/requests/rigFracActivity";
import { resolveRigFracMetrics } from "entities/charts/eva/requests/rigFracMetrics";
import { isPriceMetric } from "entities/charts/eva/translators/rigFracActivity";
import {
  RECENT_LABEL_MAX_WIDTH,
  RECENT_LABEL_MIN_WIDTH
} from "entities/charts/eva/translators/rigFracRecentActivity";
import { useAtomValue } from "jotai";
import { updateChart } from "store/features/charts/chartSlice";
import styled from "styled-components/macro";
import { ChartRequestType, RigFracActivityChartRequestType } from "types";

import { useChartEntities } from "hooks/charts";

import { DataEnum } from "models";

import { visibilityToggleStyles } from "../../shared/SharedStyles";
import { isGroupByOfferedFor } from "./groupByOptions";

// EVA-149. The two counts are valid on every selector (left, right, and
// Recent Activity's bottom); price series and "no secondary axis" only make
// sense on the right (overlay line) selector — chart-svc 400s a price/None
// primaryMetric or Recent Activity metric (FR-016). ACTIVITY_ONLY_METRIC_OPTIONS
// keeps the OLD flat list's identity/order for left + bottom so nothing about
// those two selectors changes; RIGHT_METRIC_OPTIONS adds the three new
// choices, price options ordered together, "no secondary axis" last as an
// explicit opt-out rather than mixed in among real metrics.
const ACTIVITY_ONLY_METRIC_OPTIONS = [
  { label: "Rig Count", value: RigFracActivityMetric.RigCount },
  { label: "Frac Crew Count", value: RigFracActivityMetric.FracCrewCount }
];

// EVA-105. AWS only; offered wherever a count is (left, right, bottom).
const APPROVED_PERMITS_OPTION = {
  label: "Approved Permits",
  value: RigFracActivityMetric.ApprovedPermits
};

const RIGHT_ONLY_METRIC_OPTIONS = [
  { label: "WTI ($/bbl)", value: RigFracActivityMetric.WtiPrice },
  { label: "Henry Hub ($/mmbtu)", value: RigFracActivityMetric.HenryHubPrice },
  { label: "None", value: RigFracActivityMetric.None }
];

// side-aware option list: left/bottom (Recent Activity) get counts only; right
// (the overlay line, the only field carrying a price or None) gets all of them.
const optionsForSide = (side: RigFracMetricSide) => {
  const counts = IS_AWS_ENV
    ? [...ACTIVITY_ONLY_METRIC_OPTIONS, APPROVED_PERMITS_OPTION]
    : ACTIVITY_ONLY_METRIC_OPTIONS;
  return side === "right" ? [...counts, ...RIGHT_ONLY_METRIC_OPTIONS] : counts;
};

export type RigFracMetricSide = "left" | "right" | "bottom";

interface RigFracMetricSelectorProps {
  side: RigFracMetricSide;
}

// Offset bounds mirror the adaptive band's width range: the button sits width/2
// right of centre, and width ∈ [MIN, MAX], so the offset ∈ [MIN/2, MAX/2]. Derived
// from the translator's exported constants so the two never drift.
const BOTTOM_OFFSET_MIN_PX = RECENT_LABEL_MIN_WIDTH / 2;
const BOTTOM_OFFSET_MAX_PX = RECENT_LABEL_MAX_WIDTH / 2;

/**
 * Horizontal offset (px, right of the container centre) for the Recent Activity
 * value-axis (`bottom`) button, which overlays the axis title ECharts centres on
 * the PLOT. The plot's left inset adapts to the group labels (see the translator's
 * recentCategoryLabelWidth), so the button must track it.
 *
 * `useRigFracChartOptions` persists ONLY `xAxis`/`yAxis`, never `grid`, so we read
 * the category axis's persisted `nameGap` — the same live-readback TimeScaleSelector
 * uses for `xAxis.nameGap`. The translator sets `nameGap = width + 16` and
 * `grid.left = width + 32` with `grid.right` fixed at 32, so the plot-centre offset
 * is `(grid.left − grid.right) / 2 = width / 2 = (nameGap − 16) / 2`. The result is
 * clamped to the band's legitimate range so a corrupt/foreign persisted value
 * (e.g. `Infinity`, a percentage string) can't park the button off the widget.
 * Returns 0 for the y-axis (left/right) buttons; falls back to the minimum offset
 * when `options` isn't yet persisted or is malformed.
 */
export const computeBottomOffsetPx = (
  options: string,
  side: RigFracMetricSide
): number => {
  if (side !== "bottom") return 0;
  try {
    const yAxis = JSON.parse(options || "{}")?.yAxis;
    // Recent Activity persists a single category yAxis; guard the array shape
    // (Activity Over Time) defensively even though only Recent renders `bottom`.
    const axis = Array.isArray(yAxis) ? yAxis[0] : yAxis;
    const nameGap = axis?.nameGap;
    if (typeof nameGap !== "number" || !Number.isFinite(nameGap)) {
      return BOTTOM_OFFSET_MIN_PX;
    }
    const offset = (nameGap - 16) / 2;
    return Math.min(BOTTOM_OFFSET_MAX_PX, Math.max(BOTTOM_OFFSET_MIN_PX, offset));
  } catch (error) {
    // A malformed persisted options string is a corruption signal — capture it
    // per the repo's error-handling convention rather than swallowing silently.
    Sentry.withScope((scope) => {
      scope.setContext("computeBottomOffsetPx", { options });
      scope.setLevel("warning");
      Sentry.captureException(error);
    });
    return BOTTOM_OFFSET_MIN_PX;
  }
};

/**
 * Metric pickers for the Rig/Frac charts (EVA-76). Behaves like the wells
 * "Select Field" popover: an axis-label button that opens an antd Popover on
 * click — white, arrowless, its list sitting flush against the button.
 *
 * Activity Over Time: rotated vertical buttons on the y-axes — `left` drives the
 * stacked bars (yAxis 0), `right` the overlay line (yAxis 1). Both popovers use
 * antd placement `left`, which intentionally splits them: the left button hugs
 * the widget's left edge so its popover opens OVER THE MAP, while the right
 * button hugs the right edge so its popover opens OVER THE CHART. Recent
 * Activity: a `bottom`, horizontal button sitting with the value (x) axis — the
 * metric maps to that axis on the horizontal bars — writing the single `metric`;
 * popover opens upward. Writing the selection re-triggers the POST.
 */
function RigFracMetricSelector({ side }: RigFracMetricSelectorProps) {
  const dispatch = useDispatch();
  const { id } = useChartsContext();
  const { request, screenshot, dataTable, chartType, options, locks } =
    useChartEntities(id);
  const widgetHoverMap = useAtomValue(widgetHoverAtom);
  const isHover = widgetHoverMap?.[id] ?? false;
  const [open, setOpen] = useState(false);

  // The bottom button tracks the value-axis title, which is centred on a plot
  // whose left inset adapts to the group labels — so derive the offset from the
  // live grid insets (see computeBottomOffsetPx) instead of a fixed value.
  const bottomOffsetPx = useMemo(
    () => computeBottomOffsetPx(options, side),
    [options, side]
  );

  // Lean rig/frac request rides the entity's ChartRequestType-typed field —
  // same cast pattern as the controller hook. Recent Activity stores a single
  // `metric`; Activity Over Time stores left/right metrics.
  const selections = request as unknown as Maybe<
    Partial<RigFracActivityChartRequestType> & { metric?: RigFracActivityMetric }
  >;
  const isRecent = chartType === EvaChart.RigFracRecentActivity;
  const field: "metric" | "primaryMetric" | "secondaryMetric" = isRecent
    ? "metric"
    : side === "left"
    ? "primaryMetric"
    : "secondaryMetric";
  // Through the shared resolver, not selections[field]: this component reads the
  // raw entity request, which is seeded from the saved dashboard before the
  // controller's echo lands, so a legacy-key dashboard would otherwise show the
  // default until the first response arrived.
  const value = resolveRigFracMetrics(selections)[field];
  // Side-aware list (T038) — left/bottom only ever offer the two counts, so a
  // stale/legacy selection there is always found. The fallback to the raw
  // value (not a "?") matters on the RIGHT selector: a stale saved price/None
  // selection must still render as a readable label, not crash, if a future
  // change ever narrows this list again — the rollback story (plan.md §5).
  const metricOptions = optionsForSide(side);
  const label = metricOptions.find((o) => o.value === value)?.label ?? value;

  const handleChange = (metric: RigFracActivityMetric) => {
    setOpen(false);
    // EVA-107: a rig-count-only group-by (Rig Trajectory) must not survive a move
    // away from rig count. The dropdown stops OFFERING it, but the applied value
    // would persist — leaving a chart of one flat "n/a" series (frac crews have no
    // rig), and the only clue is a dropdown still reading "Rig Trajectory". Reset
    // here rather than in the
    // group-by selector: this is where the metric changes, so the two land in one
    // dispatch instead of a render-triggered follow-up.
    // Only the metric the group-by gate READS can invalidate the dimension:
    // RigFracGroupBySelector resolves it as `isRecent ? metric : primaryMetric`. Without
    // this check the right-axis selector also reset — so rig-count bars grouped by
    // trajectory plus a frac-crew overlay line (a normal thing to want) silently
    // reverted the grouping to Operator, while the dropdown still offered it.
    const gatesGroupBy = isRecent ? field === "metric" : field === "primaryMetric";
    const groupByReset =
      gatesGroupBy &&
      selections?.groupBy != null &&
      !isGroupByOfferedFor(metric, selections.groupBy)
        ? { groupBy: DEFAULT_RIG_FRAC_ACTIVITY_REQUEST.groupBy }
        : undefined;

    // EVA-149 (T044): a persisted y2 axis lock survives a metric change today —
    // harmless between two counts of similar magnitude, but a stored y2 max of
    // (say) 600 from a rig-count series flattens a ~$60 price line onto the
    // axis floor. The right selector is the ONLY one whose field drives the y2
    // axis (Recent Activity has no y2 axis at all). Gate on the actual
    // price<->count TRANSITION, not on every secondaryMetric write — count→count
    // (e.g. Frac Crew Count -> Rig Count, or re-picking the same value) must
    // NOT clear a lock: a user comparing two counts, or a dashboard save that
    // round-trips the same selection, would otherwise silently lose a saved
    // y2Max/y2Min with no undo. `value` here is the metric BEFORE this change
    // (see `const value = selections?.[field] ?? defaultValue` above); `metric`
    // is the one being written.
    //
    // Key off what CHANGED, not on price-vs-count: WTI (~$55-95/bbl) and Henry
    // Hub (~$1.50-9/mmbtu) are both prices but not comparable, so a lock at 95
    // would survive into a ~$3 series and render it on the axis floor.
    // `value !== metric` keeps a lock when the user re-picks the same metric;
    // requiring a price on one side keeps it on count-to-count.
    //
    // Cleared in the SAME dispatch as the metric
    // write (updateChart takes a Partial<ChartEntity>, so `locks` rides
    // alongside `request` in one render pass), mirroring the groupByReset
    // pattern above. `locks` is spread first since updateChart's reducer does
    // a shallow Object.assign — a bare { y2Min, y2Max } payload would silently
    // drop any x/y (primary axis) lock already in place.
    const y2LocksReset =
      field === "secondaryMetric" &&
      value !== metric &&
      (isPriceMetric(value) || isPriceMetric(metric))
        ? {
            locks: {
              ...locks,
              y2Min: { value: undefined, type: DataEnum.Reset },
              y2Max: { value: undefined, type: DataEnum.Reset }
            }
          }
        : undefined;

    dispatch(
      updateChart({
        id,
        request: {
          ...DEFAULT_RIG_FRAC_ACTIVITY_REQUEST,
          ...selections,
          [field]: metric,
          ...groupByReset
        } as unknown as ChartRequestType,
        ...y2LocksReset
      })
    );
  };

  const visible = isHover && !screenshot?.active && !dataTable?.active;

  const popoverContent = (
    <PopoverContent>
      {metricOptions.map((option) => (
        <MenuItem
          key={option.value}
          className={classnames({ isSelected: option.value === value })}
          data-testid={`rig-frac-${side}-metric-option-${option.value}`}
          onClick={() => handleChange(option.value)}>
          {option.label}
        </MenuItem>
      ))}
    </PopoverContent>
  );

  return (
    <Wrapper
      $bottomOffset={bottomOffsetPx}
      // Mirrors the applied offset so tests can assert the value the selector
      // derives from the real persisted options (the CSS is a styled calc()).
      data-bottom-offset={bottomOffsetPx}
      className={classnames(side, { visible })}
      data-testid={`rig-frac-${side}-metric`}>
      <Popover
        open={open}
        onOpenChange={setOpen}
        trigger="click"
        // Both y-axis buttons use placement "left" (screen-left of the trigger),
        // which intentionally splits the two: the left button hugs the widget's
        // left edge so its popover opens OVER THE MAP, while the right button hugs
        // the right edge so its popover opens OVER THE CHART. autoAdjustOverflow
        // (default) only flips at the viewport edge, so the left popover stays
        // over the map instead of snapping back into the chart. The bottom
        // (value-axis) button opens up.
        placement={side === "bottom" ? "top" : "left"}
        // antd reserves ~15.3px on the placement side for the (now-hidden) arrow;
        // pull the popup back over that reserved space so the list sits flush
        // against the button. Done via the align PROP (not CSS) because it's the
        // reliable knob for the popup distance.
        align={{
          offset: side === "bottom" ? [0, 14] : [14, 0]
        }}
        overlayClassName="rig-frac-metric-popover"
        content={popoverContent}>
        <AxisButton data-testid={`rig-frac-${side}-metric-trigger`}>
          <AxisLabel>{label}</AxisLabel>
          <Icon path={mdiChevronDown} size={0.75} />
        </AxisButton>
      </Popover>
    </Wrapper>
  );
}

export default RigFracMetricSelector;

const Wrapper = styled.div<{ $bottomOffset: number }>`
  ${visibilityToggleStyles};
  position: absolute;
  z-index: 1;
  transform-origin: center;

  /* Rotated to read vertically along each y-axis, matching the wells charts'
     ChartAxisFieldSelector (its FieldContainer.yAxis rotates -90deg). Left hugs
     the primary (bars) axis, right hugs the line (y2) axis. These hover-only
     buttons change the metric and stand in for the echarts axis titles, which
     are hidden while they show (useHideAxisTitles).

     Measured against a headless ECharts render of the Activity layout:
     - the title's x anchor = GRID_LEFT_RIGHT(66) − Y_AXIS_NAME_GAP(46) = 20px
       from the edge (mirrored on the right). The transform pulls the button to
       17px — 3px inboard of that anchor;
     - the title centres on the GRID vertically — offset from the container
       centre by (GRID_TOP 46 − GRID_BOTTOM_ROTATED 100) / 2 = −27px. Centring
       the button at container 50% left it 27px low. */
  &.left {
    top: calc(50% - 27px);
    left: 0;
    transform: translate(calc(-50% + 17px), -50%) rotate(-90deg);
  }

  &.right {
    top: calc(50% - 27px);
    right: 0;
    transform: translate(calc(50% - 17px), -50%) rotate(-90deg);
  }

  /* Recent Activity: horizontal (un-rotated) button centred on the bottom value
     axis, mirroring how the y-axis buttons overlay their axis titles on hover.
     Centred on the PLOT (grid), not the container: the grid insets are
     asymmetric (left adapts to the group-label length, right 32 — see the Recent
     Activity translator), so the axis title sits ($bottomOffset =
     (grid.left − grid.right) / 2) right of the container centre. Read live from
     the persisted options so the button tracks the band as it resizes; without
     the offset the button reads visibly off-centre vs the title behind it. */
  &.bottom {
    top: unset;
    bottom: 6px;
    left: calc(50% + ${(p) => p.$bottomOffset}px);
    transform: translateX(-50%);
  }
`;

// The rotated vertical axis label; clicking opens the metric popover.
const AxisButton = styled.button`
  display: inline-flex;
  align-items: center;
  /* Hug the text so the button tracks the axis title it replaces on hover.
     max-width caps growth so an unexpectedly long metric name can't sprawl. */
  width: fit-content;
  max-width: 200px;
  justify-content: center;
  gap: 2px;
  padding: 2px 6px 2px 12px;
  border: 1px solid var(--color-surface-04);
  border-radius: var(--border-radius);
  background-color: var(--white);
  color: var(--color-text);
  font-weight: var(--fontWeightMedium);
  /* Same size as the echarts axis title it replaces (DEFAULT_AXIS_FONT_SIZE). */
  font-size: 15px;
  cursor: pointer;

  &:hover {
    border-color: var(--color-primary);
  }
`;

// Ellipsis lives on the label, not the button, so the chevron never clips.
const AxisLabel = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// The dropdown list. No inner padding/gap and clipped to the rounded corners so
// the grey highlight fills each row edge-to-edge with no surrounding whitespace
// antd's own inner-content padding + arrow are removed in
// antd.css (.rig-frac-metric-popover) so the list sits snug against the button.
const PopoverContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 140px;
  padding: 0;
  overflow: hidden;
  border-radius: var(--border-radius);
`;

const MenuItem = styled.div`
  padding: 8px 16px;
  font-size: 14px;
  font-weight: var(--fontWeightMedium);
  color: var(--color-text);
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background-color: var(--color-text-06);
  }

  /* Current metric highlighted with a neutral grey (no teal, match
     the standard EVA dropdown's grey selected row) — full-width fill (no
     border-radius) so the row highlights edge-to-edge like the mockup. */
  &.isSelected {
    background-color: var(--smoke);
    color: var(--color-text);
  }
`;
